import { MotionConfig } from 'framer-motion';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { DataProvider } from './DataProvider';
import { ToastProvider } from '@aurora-reactkit/components/toast';
import { DemoMenu } from './DemoMenu';
import { DigestOpen } from './DigestOpen';
import { PaywallProvider } from '../patterns/PaywallSheet';
import { PlanProvider } from './PlanProvider';
import { FocusShell, Shell } from './Shell';
import { ThemeProvider } from './ThemeProvider';

// Route-level code splitting: the first paint loads the shell and the current screen only.
const Footprint = lazy(() => import('../screens/Footprint'));
const Findings = lazy(() => import('../screens/Findings'));
const FindingDetail = lazy(() => import('../screens/FindingDetail'));
const Resolve = lazy(() => import('../screens/Resolve'));
const Watchlist = lazy(() => import('../screens/Watchlist'));
const Settings = lazy(() => import('../screens/Settings'));
const FreeScan = lazy(() => import('../screens/FreeScan'));
const Lab = lazy(() => import('../screens/lab/Lab'));
const Story = lazy(() => import('../screens/story/Story'));
const Welcome = lazy(() => import('../screens/welcome/Welcome'));

// After first paint, fetch the other screens while the browser is idle so navigation never waits.
const prefetch = () => { void import('../screens/Findings'); void import('../screens/FindingDetail'); void import('../screens/Resolve');
  void import('../screens/Watchlist'); void import('../screens/Settings'); void import('../screens/Footprint'); void import('../screens/FreeScan'); };
if (typeof window !== 'undefined') {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1200));
  window.addEventListener('load', () => idle(prefetch), { once: true });
}

export function App() {
  return (
    <ThemeProvider>
      <PlanProvider>
        <DataProvider>
        <MotionConfig reducedMotion="user">
          <BrowserRouter>
            <PaywallProvider>
            <Suspense fallback={null}>
              <Routes>
                <Route element={<Shell />}>
                  <Route index element={<Footprint />} />
                  <Route path="findings" element={<Findings />} />
                  <Route path="findings/:id" element={<FindingDetail />} />
                  <Route path="resolve" element={<Resolve />} />
                  <Route path="watchlist" element={<Watchlist />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="lab" element={<Lab />} />
                  <Route path="lab/:slug" element={<Lab />} />
                </Route>
                <Route element={<FocusShell />}>
                  <Route path="scan" element={<FreeScan />} />
                </Route>
                <Route path="story" element={<Story />} />
                <Route path="welcome" element={<Welcome />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <DemoMenu />
            <DigestOpen />
            <ToastProvider containerAriaLabel="Notifications" />
            </PaywallProvider>
          </BrowserRouter>
        </MotionConfig>
        </DataProvider>
      </PlanProvider>
    </ThemeProvider>
  );
}
